/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '@saviynt/design-system';
import PropTypes from 'prop-types';
import useStyles from './styles/PreviewModal.styles';
import XMLNode from './XMLNode';
import JSONNode from './JSONNode';
import {
  decodeHtmlEntities,
  tryParseJSON,
  parseXML,
  collectNodeIds,
  collectJsonIds,
} from './parsers';

function PreviewModal({ isOpen, onClose, title, data, type }) {
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState({});
  const [highlighted, setHighlighted] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const lineRef = useRef(0);

  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  const decodedData = useMemo(() => {
    if (typeof data === 'string') return decodeHtmlEntities(data);
    return data;
  }, [data]);

  const parsedXML = useMemo(() => {
    if (typeof decodedData === 'string') {
      try {
        return parseXML(decodedData);
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [decodedData]);

  const parsedJSON = useMemo(() => {
    if (parsedXML) return null;
    if (typeof decodedData === 'object' && decodedData !== null) return decodedData;
    if (typeof decodedData === 'string') return tryParseJSON(decodedData);
    return null;
  }, [decodedData, parsedXML]);

  const allNodeIds = useMemo(() => {
    if (parsedXML?.root) return collectNodeIds(parsedXML.root);
    if (parsedJSON) return collectJsonIds(parsedJSON);
    return [];
  }, [parsedXML, parsedJSON]);

  const hasTree = parsedXML || parsedJSON;

  if (!isOpen && !isClosing) return null;

  const handleToggle = (id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleHighlight = (tagName) => {
    setHighlighted((prev) => (prev === tagName ? null : tagName));
  };

  const handleCollapseAll = () => {
    const all = {};
    allNodeIds.forEach((id) => {
      all[id] = true;
    });
    setCollapsed(all);
  };

  const handleExpandAll = () => setCollapsed({});

  const getRawText = () => {
    if (typeof decodedData === 'string') return decodedData;
    return JSON.stringify(data, null, 2);
  };

  const renderTree = () => {
    if (parsedXML) {
      return (
        <>
          {parsedXML.xmlDeclaration && (
            <div className={classes.row}>
              <span className={classes.lineNumber}>1</span>
              <span className={classes.xmlDeclaration}>{parsedXML.xmlDeclaration}</span>
            </div>
          )}
          <XMLNode
            node={parsedXML.root}
            depth={0}
            collapsed={collapsed}
            onToggle={handleToggle}
            highlighted={highlighted}
            onHighlight={handleHighlight}
            classes={classes}
            lineRef={lineRef}
          />
        </>
      );
    }
    if (parsedJSON) {
      return (
        <JSONNode
          keyName={null}
          value={parsedJSON}
          depth={0}
          collapsed={collapsed}
          onToggle={handleToggle}
          classes={classes}
          lineRef={lineRef}
          isLast
        />
      );
    }
    return <pre className={classes.jsonPre}>{getRawText()}</pre>;
  };

  lineRef.current = 0;

  return (
    <div className={`${classes.overlay} ${isClosing ? classes.overlayClosing : ''}`}>
      <div className={`${classes.modal} ${isClosing ? classes.modalClosing : ''}`}>
        <div className={classes.header}>
          <h3 className={classes.title}>{title}</h3>
          <button type="button" onClick={handleClose} className={classes.closeButton}>
            <Icon kind="close" size="small" color="--color-neutral-1100" />
          </button>
        </div>
        <div className={classes.xmlContainer}>
          <div className={classes.toolbar}>
            {hasTree && (
              <>
                <button type="button" onClick={handleExpandAll} className={classes.toolbarBtn}>
                  Expand All
                </button>
                <button type="button" onClick={handleCollapseAll} className={classes.toolbarBtn}>
                  Collapse All
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(getRawText())}
              className={classes.copyButton}
            >
              <Icon kind="copy" size="small" color="--color-neutral-1100" />
              Copy
            </button>
          </div>
          <div className={classes.contentArea}>{renderTree()}</div>
        </div>
      </div>
    </div>
  );
}

PreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.any.isRequired,
  type: PropTypes.oneOf(['source', 'target']).isRequired,
};

export default PreviewModal;
